import { authenticatedFetch, authenticatedPost } from '../utils/apiUtils';
import { logDebug, logError } from '../utils/logging';
import useAuthStore from '../store/authStore';
import { fetchWithRetry, ApiClientError } from '../utils/fetchWithRetry';

// Helper function to get a robust v2 base URL without double versions
const getDynamicApiV2BaseUrl = () => {
  const state = useAuthStore.getState();
  const provided = state.apiUrls?.backendBaseUrl 
    || (window as unknown as { smartieAiData?: { backendApiBaseUrl?: string } }).smartieAiData?.backendApiBaseUrl 
    || "http://127.0.0.1:8000";

  // Normalize to root (strip any trailing /api/v1 or /api/v2)
  const apiMatch = provided.match(/^(.*)\/api\/(v1|v2)\/?$/);
  const root = apiMatch ? apiMatch[1] : provided.replace(/\/$/, "");
  return `${root}/api/v2`;
};

// TypeScript interfaces for v2 API responses
export interface Mindspace {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface ContextItem {
  id: string;
  mindspace_id: string;
  item_type: string; // Canonical key
  content: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface QuickActionTemplate {
  id: string;
  template_id: string;
  title: string;
  description: string;
  category: 'copywriting' | 'documente' | 'altele' | 'strategic';
  card_type: 'creaza_draft' | 'sfaturi_strategice';
  initial_prompt: string;
  questions: Array<{
    text: string;
    canonical_key?: string;
    template_key?: string;
    tier?: number;
    is_canonical: boolean;
  }>;
  final_prompt_template: string;
  implicit_context_keys?: string[];
}

// Normalize helpers for defensive shaping of API responses
const isGuidedQuestionArray = (
  value: unknown
): value is QuickActionTemplate['questions'] => {
  if (!Array.isArray(value)) return false;
  return value.every((q) =>
    typeof q === 'object' && q !== null && typeof (q as { text?: unknown }).text === 'string'
  );
};

const normalizeTemplate = (tpl: QuickActionTemplate): QuickActionTemplate => {
  const rawQuestions: unknown = (tpl as unknown as { questions?: unknown })?.questions;
  const questions = isGuidedQuestionArray(rawQuestions) ? rawQuestions : [];
  return {
    ...tpl,
    questions,
    final_prompt_template:
      typeof tpl.final_prompt_template === 'string' ? tpl.final_prompt_template : '',
    initial_prompt: typeof tpl.initial_prompt === 'string' ? tpl.initial_prompt : '',
  };
};

const normalizeTemplates = (list: QuickActionTemplate[]): QuickActionTemplate[] => list.map(normalizeTemplate);

export interface CreateContextItemRequest {
  item_type: string;
  content: string;
  metadata?: Record<string, unknown>;
}

export interface ChatStreamRequest {
  session_id?: string;
  message: string;
  template_id?: string;
  context_override?: Record<string, unknown>;
}

export interface ChatStreamUnifiedRequest extends ChatStreamRequest {
  mindspace_id?: string;
  session_context?: Record<string, unknown>;
}

/**
 * Get user's mindspaces (auto-creates default mindspace on first call)
 */
export async function fetchMindspaces(): Promise<Mindspace[]> {
  const API_BASE_URL = getDynamicApiV2BaseUrl();
  const url = `${API_BASE_URL}/mindspaces/`;
  
  logDebug('[apiClientV2] Fetching mindspaces from:', url);
  
  return authenticatedFetch<Mindspace[]>(url, 'apiClientV2', 'fetchMindspaces');
}

/**
 * Get specific mindspace by ID
 */
export async function fetchMindspace(mindspaceId: string): Promise<Mindspace> {
  const API_BASE_URL = getDynamicApiV2BaseUrl();
  const url = `${API_BASE_URL}/mindspaces/${mindspaceId}`;
  
  logDebug('[apiClientV2] Fetching mindspace:', mindspaceId);
  
  return authenticatedFetch<Mindspace>(url, 'apiClientV2', 'fetchMindspace');
}

/**
 * Get context items for a mindspace
 */
export async function fetchContextItems(mindspaceId: string): Promise<ContextItem[]> {
  const API_BASE_URL = getDynamicApiV2BaseUrl();
  const url = `${API_BASE_URL}/mindspaces/${mindspaceId}/context-items`;
  
  logDebug('[apiClientV2] Fetching context items for mindspace:', mindspaceId);
  
  return authenticatedFetch<ContextItem[]>(url, 'apiClientV2', 'fetchContextItems');
}

/**
 * Create or update context item (upsert for canonical keys)
 */
export async function createContextItem(
  mindspaceId: string, 
  contextItem: CreateContextItemRequest
): Promise<ContextItem> {
  const API_BASE_URL = getDynamicApiV2BaseUrl();
  const url = `${API_BASE_URL}/mindspaces/${mindspaceId}/context-items`;
  
  logDebug('[apiClientV2] Creating/updating context item for mindspace:', mindspaceId, contextItem);
  
  return authenticatedPost<ContextItem>(url, contextItem, 'apiClientV2', 'createContextItem');
}

/**
 * Get quick action templates with optional filtering
 */
export async function fetchQuickActionTemplates(params?: {
  category?: 'copywriting' | 'documente' | 'altele' | 'strategic';
  card_type?: 'creaza_draft' | 'sfaturi_strategice';
}): Promise<QuickActionTemplate[]> {
  const API_BASE_URL = getDynamicApiV2BaseUrl();
  
  // Build query parameters
  const searchParams = new URLSearchParams();
  if (params?.category) {
    searchParams.append('category', params.category);
  }
  if (params?.card_type) {
    searchParams.append('card_type', params.card_type);
  }
  
  const url = `${API_BASE_URL}/quick-actions/${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
  
  logDebug('[apiClientV2] Fetching quick action templates:', url, params);
  
  try {
    const response = await fetchWithRetry(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      retries: 3,
      retryDelay: 1000,
      retryOn: () => true,
    });

    const data = await response.json();
    logDebug('[apiClientV2] Quick actions response:', data);
    return Array.isArray(data) ? normalizeTemplates(data) : [];
  } catch (error) {
    logError('[apiClientV2] Error fetching quick action templates:', error);
    // Re-throw as ApiClientError if network or other unknown
    if (!(error instanceof ApiClientError)) {
      throw new ApiClientError('Failed to fetch quick action templates', undefined);
    }
    throw error;
  }
}

/**
 * Get specific quick action template by ID
 */
export async function fetchQuickActionTemplate(templateId: string): Promise<QuickActionTemplate> {
  const API_BASE_URL = getDynamicApiV2BaseUrl();
  const url = `${API_BASE_URL}/quick-actions/${templateId}`;
  
  logDebug('[apiClientV2] Fetching quick action template:', templateId);
  
  // Public endpoint, no auth required
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      logError('[apiClientV2] Quick action template fetch failed:', response.status, errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    logDebug('[apiClientV2] Quick action template response:', data);
    return normalizeTemplate(data as QuickActionTemplate);
  } catch (error) {
    logError('[apiClientV2] Error fetching quick action template:', error);
    throw error;
  }
}

/**
 * Stream chat with context (v2 endpoint)
 */
export async function streamChatWithContext(
  mindspaceId: string,
  requestData: ChatStreamRequest,
  onToken: (chunk: string) => void,
  onEnd: () => void,
  onError: (errorMessage: string | Error) => void,
  onStreamControllerCreated: (controller: AbortController | null) => void,
): Promise<void> {
  const API_BASE_URL = getDynamicApiV2BaseUrl();
  const url = `${API_BASE_URL}/mindspaces/${mindspaceId}/chat/stream`;
  
  logDebug('[apiClientV2] Starting context-aware chat stream:', mindspaceId, requestData);
  
  // Import the SSE utility dynamically to avoid circular imports
  const { fetchAndProcessSSE } = await import('../utils/sseUtils');
  
  const abortController = new AbortController();
  onStreamControllerCreated(abortController);
  
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  try {
    reader = await fetchAndProcessSSE({
      url,
      method: "POST",
      body: requestData,
      signal: abortController.signal,
      // v2 endpoints require authentication (handled by manual auth header)
      onOpen: () => {
        logDebug("[apiClientV2] SSE stream opened for context-aware chat.");
      },
      onMessage: (data, eventType) => {
        if (eventType === "token" || !eventType) {
          try {
            logDebug(`[apiClientV2] Received token data:`, data);
            const parsedData = JSON.parse(data);
            if (parsedData && typeof parsedData.token === "string") {
              onToken(parsedData.token);
            } else {
              logError("[apiClientV2] Invalid token data structure:", parsedData);
            }
          } catch (e) {
            logError("[apiClientV2] Failed to parse token data:", e, data);
          }
        }
      },
      onEnd: () => {
        onEnd();
        onStreamControllerCreated(null);
      },
      onError: (err) => {
        const error = err instanceof Error ? err : new Error(String(err));
        onError(error);
        onStreamControllerCreated(null);
      },
    });
  } catch (error) {
    logError("[apiClientV2] Error in streamChatWithContext:", error);
    const errToReport = error instanceof Error ? error : new Error(String(error));
    onError(errToReport);
    onStreamControllerCreated(null);
    if (!abortController.signal.aborted) {
      abortController.abort();
    }
  } finally {
    if (reader && !abortController.signal.aborted) {
      try {
        await reader.cancel("streamChatWithContext cleanup");
        reader.releaseLock();
        logDebug("[apiClientV2] Reader cancelled and released.");
      } catch (e) {
        logError("[apiClientV2] Error cleaning up reader:", e);
      }
    }
  }
}

/**
 * Stream chat via unified v2 endpoint (guest-friendly).
 *
 * Behaviour:
 *   - If `mindspace_id` is provided, backend will use it (authenticated users).
 *   - If omitted (guests), backend falls back to session-only context.
 *
 * The caller is expected to build `requestData` with appropriate fields and, for guests,
 * exclude `mindspace_id` while including `session_context` populated from guided flow answers.
 */
export async function streamChat(
  requestData: ChatStreamUnifiedRequest,
  onToken: (chunk: string) => void,
  onEnd: () => void,
  onError: (errorMessage: string | Error) => void,
  onStreamControllerCreated: (controller: AbortController | null) => void,
): Promise<void> {
  const API_BASE_URL = getDynamicApiV2BaseUrl();
  const url = `${API_BASE_URL}/chat/stream`;

  logDebug('[apiClientV2] Starting unified chat stream:', requestData);

  // Import SSE utility lazily to avoid circular deps
  const { fetchAndProcessSSE } = await import('../utils/sseUtils');

  const abortController = new AbortController();
  onStreamControllerCreated(abortController);

  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  try {
    reader = await fetchAndProcessSSE({
      url,
      method: 'POST',
      body: requestData,
      signal: abortController.signal,
      onOpen: () => {
        logDebug('[apiClientV2] SSE stream opened for unified chat.');
      },
      onMessage: (data, eventType) => {
        if (eventType === 'token' || !eventType) {
          try {
            logDebug('[apiClientV2] Received token data:', data);
            const parsedData = JSON.parse(data);
            if (parsedData && typeof parsedData.token === 'string') {
              onToken(parsedData.token);
            } else {
              logError('[apiClientV2] Invalid token data structure:', parsedData);
            }
          } catch (e) {
            logError('[apiClientV2] Failed to parse token data:', e, data);
          }
        }
      },
      onEnd: () => {
        onEnd();
        onStreamControllerCreated(null);
      },
      onError: (err) => {
        const error = err instanceof Error ? err : new Error(String(err));
        onError(error);
        onStreamControllerCreated(null);
      },
    });
  } catch (error) {
    logError('[apiClientV2] Error in streamChat (unified):', error);
    const errToReport = error instanceof Error ? error : new Error(String(error));
    onError(errToReport);
    onStreamControllerCreated(null);
    if (!abortController.signal.aborted) {
      abortController.abort();
    }
  } finally {
    if (reader && !abortController.signal.aborted) {
      try {
        await reader.cancel('streamChat cleanup');
        reader.releaseLock();
        logDebug('[apiClientV2] Reader cancelled and released.');
      } catch (e) {
        logError('[apiClientV2] Error cleaning up reader:', e);
      }
    }
  }
} 